import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { SupabaseIcon } from '@/components/SupabaseIcon';
import WorkshopStepWrapper from '../WorkshopStepWrapper';
import { useWorkshopProgress } from '@/contexts/WorkshopProgressContext';
import { Sparkles } from 'lucide-react';

const originalContent = `Dear Supporter,

Thank you for your donation to our organization. Your support helps us continue our important work in the community. We appreciate your generosity and look forward to updating you on our progress.

Best regards,
The Team`;

const improvedContent = `Dear Sarah,

Your $75 monthly gift directly funded Marcus's breakthrough! This 9-year-old advanced from grade 1 to grade 3 reading level in just 4 months through our tutoring program.

Marcus told me yesterday: "I can finally read chapter books!" His mom shared that he's now reading bedtime stories to his little sister.

Would you like to visit our Tuesday tutoring session to see the magic in action?

With deep gratitude,
Maya Rodriguez
Program Coordinator, Hope Gardens Community Center`;

export const WorkshopTransformation: React.FC = () => {
  const { navigateToStep, masterSkill } = useWorkshopProgress();

  const handleContinue = () => {
    masterSkill(2); // Voice Alignment
    masterSkill(3); // Quality Control
    navigateToStep('assessment');
  };

  return (
    <WorkshopStepWrapper
      step="transformation"
      title="Transformation Reveal"
      description="See what YOU created vs. professional AI direction"
      showSkillsPreview={true}
      autoMarkComplete={true}
      requiredSkills={[2, 3]}
    >
      {/* Lyra's Celebration */}
      <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
        <div className="flex items-start space-x-6">
          <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center">
            <SupabaseIcon
              iconPath="lyra-avatar.png"
              alt="Lyra"
              size="lg"
              className="w-12 h-12"
            />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-green-800 mb-3">Excellent Work!</h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              "You just practiced the core skills of professional AI direction! Let me show you how your
              approach compares to what professional AI mastery looks like. Notice how the principles
              you practiced - specific context, real impact, authentic voice - create dramatic transformation."
            </p>
            <div className="bg-green-50 border border-green-200 p-4 rounded-xl">
              <div className="text-green-800 font-semibold">What You Mastered:</div>
              <div className="text-green-700 mt-2">Context setting → Impact specification → Voice alignment</div>
            </div>
          </div>
        </div>
      </div>

      {/* Before and After Comparison */}
      <div className="grid md:grid-cols-2 gap-8 mb-8">
        {/* Before */}
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Before: Generic AI Output</CardTitle>
            <CardDescription>What AI gave us initially</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-white p-4 rounded-xl border text-sm text-gray-700 leading-relaxed">
              {originalContent}
            </div>
            <div className="mt-4 text-xs text-red-600 space-y-1">
              <div>❌ Generic language</div>
              <div>❌ No specific impact</div>
              <div>❌ Corporate tone</div>
              <div>❌ No engagement</div>
            </div>
          </CardContent>
        </Card>

        {/* After */}
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-700">After: Professional AI Direction</CardTitle>
            <CardDescription>Using the techniques you just practiced</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-white p-4 rounded-xl border text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {improvedContent}
            </div>
            <div className="mt-4 text-xs text-green-600 space-y-1">
              <div>✅ Personal and specific</div>
              <div>✅ Concrete impact story</div>
              <div>✅ Authentic voice</div>
              <div>✅ Clear engagement</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Impact Metrics */}
      <Card className="mb-8 bg-gradient-to-r from-purple-50 to-green-50 border-purple-200">
        <CardContent className="pt-6">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">The Transformation Impact</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">10x</div>
                <div className="text-gray-600 text-sm">More Engaging</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">85%</div>
                <div className="text-gray-600 text-sm">More Specific</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">100%</div>
                <div className="text-gray-600 text-sm">Mission-Aligned</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-center">
        <Button
          onClick={handleContinue}
          size="lg"
          className="bg-green-600 hover:bg-green-700"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          See What You've Mastered →
        </Button>
      </div>
    </WorkshopStepWrapper>
  );
};

export default WorkshopTransformation;